import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Linkedin } from "lucide-react";
import { useLang } from "@/context";
import { Chapter, MaskedLine, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", position: "", country: "", message: "" };

export default function Contact() {
  const { t, service, setService } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service });
      toast.success(t.contact.success);
      setForm(EMPTY);
      setService("");
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-taupe/40 focus:border-gold outline-none py-4 text-base text-charcoal placeholder:text-taupe/70 font-light transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 sm:py-40">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Chapter number={t.contact.chapter} label={t.contact.label} />

        <div className="grid grid-cols-12 gap-y-12 lg:gap-20">
          <div className="col-span-12 lg:col-span-6 min-w-0">
            <h2
              data-testid="contact-headline"
              className="font-serif text-4xl sm:text-6xl lg:text-[4.2rem] text-charcoal font-medium leading-[1.06]"
            >
              {t.contact.lines.map((line, i) => (
                <MaskedLine key={line} delay={i * 0.15}>
                  {i === 1 ? <span className="italic text-charcoal/85">{line}</span> : line}
                </MaskedLine>
              ))}
            </h2>

            <Reveal delay={0.3}>
              <p data-testid="contact-intro1" className="mt-10 font-serif italic text-2xl sm:text-3xl text-charcoal/90">
                {t.contact.intro1}
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <p data-testid="contact-intro2" className="mt-5 max-w-md text-sm sm:text-base text-ink/70 leading-relaxed font-light">
                {t.contact.intro2}
              </p>
            </Reveal>

            <Reveal delay={0.55}>
              <a
                data-testid="contact-linkedin-link"
                href="https://www.linkedin.com/in/mercellina/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-9 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.26em] text-taupe hover:text-gold transition-colors duration-300"
              >
                <Linkedin size={14} className="text-gold" />
                {t.contact.linkedin}
              </a>
            </Reveal>

            <Reveal delay={0.5}>
              <div data-testid="contact-tags" className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.26em] text-taupe">
                {t.contact.tags.map((tag, i) => (
                  <span key={tag} className="flex items-center gap-3">
                    {i > 0 && <span className="w-1 h-1 bg-gold rotate-45 inline-block" aria-hidden="true" />}
                    {tag}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-6 min-w-0">
            <Reveal delay={0.25}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7"
              >
                <input
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.form.name}
                  className={inputCls}
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.form.email}
                  className={inputCls}
                />
                <input
                  data-testid="contact-company-input"
                  value={form.company}
                  onChange={set("company")}
                  placeholder={t.contact.form.company}
                  className={inputCls}
                />
                <input
                  data-testid="contact-position-input"
                  value={form.position}
                  onChange={set("position")}
                  placeholder={t.contact.form.position}
                  className={inputCls}
                />
                <input
                  data-testid="contact-country-input"
                  value={form.country}
                  onChange={set("country")}
                  placeholder={t.contact.form.country}
                  className={inputCls}
                />
                <div className="relative">
                  <select
                    data-testid="contact-service-select"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className={`${inputCls} appearance-none cursor-pointer pr-8 ${
                      service ? "text-charcoal" : "text-taupe/70"
                    }`}
                  >
                    <option value="">{t.contact.form.service}</option>
                    {t.contact.services.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={15}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-taupe pointer-events-none"
                  />
                </div>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={t.contact.form.message}
                  className={`${inputCls} resize-none sm:col-span-2`}
                />
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
                  <p data-testid="contact-privacy" className="text-[10px] uppercase tracking-[0.22em] text-taupe/80">
                    {t.contact.privacy}
                  </p>
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={sending}
                    className="bg-charcoal text-ivory border border-charcoal px-10 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-gold hover:border-gold hover:text-charcoal transition-colors duration-300 disabled:opacity-50"
                  >
                    {sending ? t.contact.form.sending : t.contact.cta}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
